import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, UserPlus } from 'lucide-react';

import { useRequirePermission } from '@/shared/hooks/useRequirePermission';
import { MedicalStaffEditPanel } from '@/features/medical-staff/components/MedicalStaffEditPanel';
import { MedicalStaffInvitePanel } from '@/features/medical-staff/components/MedicalStaffInvitePanel';
import { MedicalStaffListFilters } from '@/features/medical-staff/components/MedicalStaffListFilters';
import { MedicalStaffListView } from '@/features/medical-staff/components/MedicalStaffListView';
import { useExportMedicalStaff } from '@/features/medical-staff/hooks/use-medical-staff-mutations';
import { useTranslateOption } from '@/features/medical-staff/hooks/use-translate-option';
import { useMedicalStaffListStore } from '@/features/medical-staff/store/medical-staff-list.store';
import type { MedicalStaff } from '@/features/medical-staff/types/medical-staff.types';
import { formatRegistrationDate } from '@/features/medical-staff/utils/format-date';
import { toast } from '@/components/feedback/useToast';
import { csvFilename, downloadCSV } from '@/shared/utils/csv';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

// Slice 8: client-side CSV export of the currently filtered + sorted list.
// PRD 03 §3.4 — filename `medical_staff_YYYYMMDD_HHmmss.csv`; columns ID, Name,
// Email, Phone, Department, Job Role, Reg. Date, Status (locale-aware).
export function MedicalStaffManagementPage() {
  const { t } = useTranslation('admin');
  const canRead = useRequirePermission('STAFF', 'READ');
  const { canWrite, denyProps, tooltip } = useWriteGuard('STAFF');

  const filters = useMedicalStaffListStore((s) => s.filters);
  const sort = useMedicalStaffListStore((s) => s.sort);
  const exportMutation = useExportMedicalStaff();
  const { translateDepartment, translateJobRole } = useTranslateOption();

  const [inviteOpen, setInviteOpen] = useState(false);

  if (!canRead) return null;

  const writeTooltip = t('medicalStaffManagement.tooltips.noWritePermission');

  const handleDownloadCsv = async () => {
    try {
      const rows = await exportMutation.mutateAsync({
        q: filters.search.trim() || undefined,
        department: filters.department || undefined,
        sortBy: sort.key,
        sortDir: sort.dir,
      });
      const headers = [
        t('medicalStaffManagement.list.columns.id'),
        t('medicalStaffManagement.list.columns.name'),
        t('medicalStaffManagement.list.columns.email'),
        t('medicalStaffManagement.list.columns.phone'),
        t('medicalStaffManagement.list.columns.department'),
        t('medicalStaffManagement.list.columns.jobRole'),
        t('medicalStaffManagement.list.columns.registeredAt'),
        t('medicalStaffManagement.list.columns.status'),
      ];
      const csvRows = rows.map((s: MedicalStaff) => [
        s.id,
        s.name,
        s.email,
        s.phone ?? '-',
        translateDepartment(s.department),
        translateJobRole(s.jobRole),
        formatRegistrationDate(s.registeredAt),
        t(`staffStatus.${s.status}`, { ns: 'labels' }),
      ]);
      downloadCSV(csvFilename('medical_staff'), headers, csvRows);
    } catch {
      // System/unknown API failures show the action-specific message, not the raw
      // mapped `e.message` (which is the generic UNKNOWN fallback).
      toast.error(t('medicalStaffManagement.toasts.downloadError'));
    }
  };

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{t('medicalStaffManagement.title')}</h1>
        </div>
        <div className="content__actions">
          {/* SHCT-656 — the 📧 / 🔑 email-preview shortcuts are gone. They were ported
              faithfully from the prototype (institution-managers.html, opacity:0.6), but
              the PRD does not have them: PRD 03 §2.1 defines this header as
              "다운로드 CSV, + 의료진 초대" and §2.2 lists exactly those two components. PRD
              outranks prototype, so they were QA scaffolding shipped by accident.
              The preview routes themselves stay — the PRD-mandated entry points are inside
              the panels (the invite panel's own preview modal), not this header. */}
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={handleDownloadCsv}
            disabled={!canWrite || exportMutation.isPending}
            {...denyProps}
            title={!canWrite ? writeTooltip : undefined}
          >
            <Download size={16} />
            {t('medicalStaffManagement.actions.downloadCsv')}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            disabled={!canWrite}
            {...denyProps}
            title={!canWrite ? writeTooltip : undefined}
            onClick={() => setInviteOpen(true)}
          >
            <UserPlus size={16} />
            {t('medicalStaffManagement.actions.inviteStaff')}
          </button>
        </div>
      </div>
      <div className="content__body">
        <MedicalStaffListFilters />
        <MedicalStaffListView />
      </div>
      <MedicalStaffInvitePanel open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <MedicalStaffEditPanel />
    </>
  );
}
